import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { GTMService } from 'src/app/services/gtm.service';
import { slugify } from '../../utils/string';

@Component({
  selector: 'modal-header',
  template: `
      <ng-content></ng-content>`
})
export class ModalHeader {
}

@Component({
  selector: 'modal-content',
  template: `
      <ng-content></ng-content>`
})
export class ModalContent {
}

@Component({
  selector: 'modal-footer',
  template: `
      <div class="modal-footer">
          <ng-content></ng-content>
      </div>`
})
export class ModalFooter {
}

@Component({
  selector: 'modal',
  template: `
      <div
              class="modal fade"
              tabindex="-1"
              role="dialog"
              #modalRoot
              (keydown.esc)="closeOnEscape ? close() : 0"
              [ngClass]="{ in: showModal }"
              [ngStyle]="{ display: isOpened ? 'block' : 'none' }"
              (click)="closeOnOutsideClick ? close() : 0">
          <div [class]="'modal-dialog ' + modalClass" (click)="preventClosing($event)">
              <div 
                class="modal-content"
                tabindex="0"
                *ngIf="isOpened"
                [class.background]="showBackground ? true : false"
                [style.backgroundImage]="'url('+backgroundImage+')'"
              >
                  <div class="modal-header">
                      <button *ngIf="!hideCloseButton" type="button" class="close" data-dismiss="modal"
                              [attr.aria-label]="cancelButtonLabel || 'Close'" (click)="close()">
                      <svg width="15px" version="1.1" xmlns="http://www.w3.org/2000/svg" height="15px" viewBox="0 0 64 64" xmlns:xlink="http://www.w3.org/1999/xlink" enable-background="new 0 0 64 64"><g><path fill="#000000" d="M28.941,31.786L0.613,60.114c-0.787,0.787-0.787,2.062,0,2.849c0.393,0.394,0.909,0.59,1.424,0.59   c0.516,0,1.031-0.196,1.424-0.59l28.541-28.541l28.541,28.541c0.394,0.394,0.909,0.59,1.424,0.59c0.515,0,1.031-0.196,1.424-0.59   c0.787-0.787,0.787-2.062,0-2.849L35.064,31.786L63.41,3.438c0.787-0.787,0.787-2.062,0-2.849c-0.787-0.786-2.062-0.786-2.848,0   L32.003,29.15L3.441,0.59c-0.787-0.786-2.061-0.786-2.848,0c-0.787,0.787-0.787,2.062,0,2.849L28.941,31.786z"/></g></svg>
                      </button>
                      <h4 class="modal-title" *ngIf="title">{{ title }}</h4>
                      <ng-content select="modal-header"></ng-content>
                  </div>
                  <div class="modal-body">
                      <ng-content select="modal-content"></ng-content>
                  </div>

                  <ng-content select="modal-footer"></ng-content>

              </div>
          </div>
      </div>
  `
})
export class Modal implements OnInit, OnDestroy {

  // -------------------------------------------------------------------------
  // Inputs
  // -------------------------------------------------------------------------

  @Input()
  public modalClass: string;

  @Input()
  public closeOnEscape = true;

  @Input()
  public closeOnOutsideClick = false;

  @Input()
  public title: string;

  @Input()
  public hideCloseButton = false;

  @Input()
  public cancelButtonLabel: string;

  @Input()
  public submitButtonLabel: string;

  @Input()
  public backdrop = false;

  @Input()
  public showModal = false;

  @Input()
  public backgroundImage: string;

  @Input()
  public showBackground = false;

  // -------------------------------------------------------------------------
  // Outputs
  // -------------------------------------------------------------------------

  @Output()
  public onOpen = new EventEmitter(false);

  @Output()
  public onClose = new EventEmitter(false);

  @Output()
  public onSubmit = new EventEmitter(false);

  // -------------------------------------------------------------------------
  // Public properties
  // -------------------------------------------------------------------------

  public isOpened = false;

  // -------------------------------------------------------------------------
  // Private properties
  // -------------------------------------------------------------------------

  @ViewChild('modalRoot')
  public modalRoot: ElementRef;

  private backdropElement: HTMLElement;

  // -------------------------------------------------------------------------
  // Constructor
  // -------------------------------------------------------------------------
  constructor(
    private gtm: GTMService
  ) {}

  // -------------------------------------------------------------------------
  // Lifecycle Methods
  // -------------------------------------------------------------------------

  ngOnInit() {
    this.createBackDrop();
  }

  ngOnDestroy() {
    document.body.className = document.body.className.replace(/modal-open\b/, '');
    if (this.backdropElement && this.backdropElement.parentNode === document.body) {
      document.body.removeChild(this.backdropElement);
    }
  }

  // -------------------------------------------------------------------------
  // Public Methods
  // -------------------------------------------------------------------------

  open(...args: any[]) {
    if (this.isOpened) {
      return;
    }

    this.isOpened = true;
    this.onOpen.emit(...args);

    window.setTimeout(() => this.modalRoot.nativeElement.focus(), 0);
    document.body.className += ' modal-open';

    setTimeout(() => {
      this.showModal = true;

      if (this.backdropElement) {
        document.body.appendChild(this.backdropElement);
      }
    }, 100);
  }

  close(...args: any[]) {
    if (!this.isOpened) {
      return;
    }

    this.showModal = false;
    document.body.removeChild(this.backdropElement);

    setTimeout(() => {
      this.isOpened = false;
      this.onClose.emit(args);
      document.body.className = document.body.className.replace(/modal-open\b/, '');
      this.trackingEventButton('close-modal');
    }, 100);
  }

  // -------------------------------------------------------------------------
  // Private Methods
  // -------------------------------------------------------------------------

  public preventClosing(event: MouseEvent) {
    event.stopPropagation();
  }

  private createBackDrop() {
    this.backdropElement = document.createElement('div');
    this.backdropElement.classList.add('fade');
    this.backdropElement.classList.add('in');

    if (this.backdrop) {
      this.backdropElement.classList.add('modal-backdrop');
    }
  }

  trackingEventButton(buttonName: string, partnerSlug = '') {
    const eventContent: any = {
      event: 'interaction',
      'event-category': 'admin',
      'event-action': `${slugify(partnerSlug)}:click`,
      'event-label': `botao:${slugify(buttonName)}`,
    };
    this.gtm.sendEvent(eventContent);
  }

}
